import React from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  Briefcase,
  Download,
  Globe,
  HardHat,
  Users,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Modal from './UI/Modal';
import {
  CHART_AXIS_COLOR,
  CHART_GRID_COLOR,
  CHART_TOOLTIP_STYLE,
} from '../config/dashboardConstants';
import { formatDateTimeCompact, getTodayInSingaporeTimezone } from '../utils/dateUtils';

// Match ClassificationBadge / classificationConfig solid fills:
// Staff #1e3a8a → blue-800, Worker #16a34a → green-600, Advisor #9333ea → purple-600.
// PMET is a separate axis and must not reuse Staff/Worker hues.
const METRIC_TONES = {
  red: {
    iconShell: 'bg-red-100',
    icon: 'text-red-600',
  },
  blue: {
    iconShell: 'bg-blue-100',
    icon: 'text-blue-800',
  },
  green: {
    iconShell: 'bg-green-100',
    icon: 'text-green-600',
  },
  indigo: {
    iconShell: 'bg-indigo-100',
    icon: 'text-indigo-600',
  },
};

const SummaryMetric = ({ value, label, sub, icon: Icon, tone }) => {
  const colors = METRIC_TONES[tone];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-4">
      <div className="flex items-center min-w-0">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colors.iconShell}`}>
          <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${colors.icon}`} aria-hidden="true" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold leading-none text-gray-900 tabular-nums">{value}</p>
          <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1 truncate">{label}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 truncate">{sub}</p>
        </div>
      </div>
    </div>
  );
};

// Pass-type palette stays distinct from employment-classification colours so
// Local/Foreign residency is not mistaken for Staff/Worker identity chips.
// Core buckets stay visible at 0; residual/gap buckets only appear when > 0.
const PASS_BREAKDOWN = [
  { key: 'singapore_citizen', label: 'Singapore Citizens', color: 'bg-sky-600', chartColor: '#0284c7', alwaysShow: true },
  { key: 'permanent_resident', label: 'Permanent Residents', color: 'bg-emerald-600', chartColor: '#059669', alwaysShow: true },
  { key: 'employment_pass', label: 'Employment Pass', color: 'bg-orange-600', chartColor: '#ea580c', alwaysShow: true },
  { key: 's_pass', label: 'S-Pass', color: 'bg-amber-500', chartColor: '#f59e0b', alwaysShow: true },
  { key: 'work_permit', label: 'Work Permit', color: 'bg-violet-600', chartColor: '#7c3aed', alwaysShow: true },
  { key: 'other', label: 'Other pass types', color: 'bg-slate-500', chartColor: '#64748b', alwaysShow: false },
  { key: 'unclassified', label: 'Not recorded', color: 'bg-gray-300', chartColor: '#d1d5db', alwaysShow: false },
];

const percentage = (value, total) => (
  total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
);

const visibleBreakdownItems = (items) => (
  items.filter((item) => item.alwaysShow || item.value > 0)
);

const BreakdownList = ({ items, total }) => (
  <div className="space-y-1">
    {items.map((item) => (
      <div
        key={item.label}
        className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.color}`} aria-hidden="true" />
          <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">{item.label}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-gray-500 tabular-nums">{percentage(item.value, total)}%</span>
          <span className="text-sm font-semibold text-gray-900 w-8 text-right tabular-nums">{item.value}</span>
        </div>
      </div>
    ))}
  </div>
);

const WorkforceCompositionModal = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  const { current, historical } = data;
  const activeTotal = current.active_workforce_total ?? current.total_workforce ?? 0;
  const classification = current.employment_classification || {};
  const pmet = current.pmet || {};
  const pmetAvailable = pmet.available !== false;
  const workAuthorisation = current.work_authorisation || {
    singapore_citizen: current.singapore_citizen || 0,
    permanent_resident: current.permanent_resident || 0,
    employment_pass: current.employment_pass || 0,
    s_pass: current.s_pass || 0,
    work_permit: current.work_permit || 0,
    other: 0,
    unclassified: 0,
  };

  const classificationItems = visibleBreakdownItems([
    { label: 'Staff', value: classification.staff || 0, color: 'bg-blue-800', alwaysShow: true },
    { label: 'Workers', value: classification.workers || 0, color: 'bg-green-600', alwaysShow: true },
    { label: 'Other classifications', value: classification.other || 0, color: 'bg-purple-600', alwaysShow: false },
    { label: 'Not classified', value: classification.unclassified || 0, color: 'bg-gray-300', alwaysShow: false },
  ]);
  const pmetItems = pmetAvailable
    ? visibleBreakdownItems([
        { label: 'PMET', value: pmet.pmet || 0, color: 'bg-indigo-600', alwaysShow: true },
        { label: 'Non-PMET', value: pmet.non_pmet || 0, color: 'bg-slate-500', alwaysShow: true },
        {
          label: 'Pending occupation review',
          value: pmet.pending_review ?? pmet.not_assessed ?? 0,
          color: 'bg-gray-300',
          alwaysShow: false,
        },
      ])
    : [
        { label: 'Assessment records unavailable', value: activeTotal, color: 'bg-gray-300' },
      ];

  const passBreakdownItems = visibleBreakdownItems(
    PASS_BREAKDOWN.map((item) => ({
      ...item,
      value: workAuthorisation[item.key] || 0,
    }))
  );

  // Chart only renders non-zero segments; a 0% slice has no visual meaning.
  const passTypeData = passBreakdownItems
    .filter((item) => item.value > 0)
    .map((item) => ({
      name: item.label,
      value: item.value,
      color: item.chartColor,
    }));

  const trendData = historical && historical.length > 0
    ? [...historical].reverse().map((snapshot) => ({
        month: new Date(snapshot.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        local: snapshot.total_local,
        foreign: snapshot.total_foreign,
      }))
    : [];

  const dataGapCount = (classification.unclassified || 0)
    + (pmet.pending_review ?? pmet.not_assessed ?? 0)
    + (workAuthorisation.unclassified || 0);

  const handleExport = () => {
    // Export keeps the full taxonomy, including zero residual buckets, for audit.
    const exportClassification = [
      { label: 'Staff', value: classification.staff || 0 },
      { label: 'Workers', value: classification.workers || 0 },
      { label: 'Other classifications', value: classification.other || 0 },
      { label: 'Not classified', value: classification.unclassified || 0 },
    ];
    const exportPmet = pmetAvailable
      ? [
          { label: 'PMET', value: pmet.pmet || 0 },
          { label: 'Non-PMET', value: pmet.non_pmet || 0 },
          { label: 'Pending occupation review', value: pmet.pending_review ?? pmet.not_assessed ?? 0 },
        ]
      : [{ label: 'Assessment records unavailable', value: activeTotal }];

    const csvContent = [
      ['Workforce Composition', 'Count', 'Percentage of Active Workforce'],
      ['Total Active Workforce', activeTotal, '100.0%'],
      [],
      ['Employment Classification', '', ''],
      ...exportClassification.map((item) => [item.label, item.value, `${percentage(item.value, activeTotal)}%`]),
      [],
      ['PMET Assessment', '', ''],
      ...exportPmet.map((item) => [item.label, item.value, `${percentage(item.value, activeTotal)}%`]),
      [],
      ['Residency / Work Authorisation', '', ''],
      ...PASS_BREAKDOWN.map((item) => [
        item.label,
        workAuthorisation[item.key] || 0,
        `${percentage(workAuthorisation[item.key] || 0, activeTotal)}%`,
      ]),
      [],
      ['Total Local', current.total_local, `${current.local_percentage}%`],
      ['Total Foreign', current.total_foreign, `${current.foreign_percentage}%`],
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workforce-composition-${getTodayInSingaporeTimezone()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      theme="admin"
      customHeader={
        <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between rounded-t-2xl gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Globe className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-2xl font-bold text-gray-900 truncate">Workforce Composition</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Classification, PMET and work-authorisation profile</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="icon-button-admin focus-visible:ring-2 focus-visible:ring-red-500"
              aria-label="Export to CSV"
              title="Export to CSV"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="modal-close-admin"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      }
      contentClassName="px-4 py-4 sm:px-8 sm:py-6"
    >
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <SummaryMetric value={activeTotal} label="Active Workforce" sub="Current scope" icon={Users} tone="red" />
          <SummaryMetric value={classification.staff || 0} label="Staff" sub="Exact classification" icon={Briefcase} tone="blue" />
          <SummaryMetric value={classification.workers || 0} label="Workers" sub="Exact classification" icon={HardHat} tone="green" />
          <SummaryMetric
            value={pmetAvailable ? (pmet.pmet || 0) : '—'}
            label="PMETs"
            sub={pmetAvailable ? `${pmet.assessed || 0} occupations classified` : 'Classification unavailable'}
            icon={BadgeCheck}
            tone="indigo"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs text-gray-500">
          <span>{current.scope || 'Employees visible in the HR portal'}</span>
          <span className="tabular-nums">
            {current.generated_at
              ? `Generated ${formatDateTimeCompact(current.generated_at)}`
              : 'Live workforce data'}
          </span>
        </div>

        {dataGapCount > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Data completeness needs attention</p>
              <p className="text-xs sm:text-sm text-amber-800 mt-0.5">
                {pmetAvailable
                  ? 'Some employees are unclassified, have no work-authorisation status, or have job titles pending SSOC review. Counts remain visible below instead of being excluded.'
                  : 'PMET classification is unavailable. Employment classification and work-authorisation counts remain available, and PMET is shown as unavailable instead of reporting a misleading zero.'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" aria-labelledby="employment-classification-heading">
            <h3 id="employment-classification-heading" className="text-sm sm:text-lg font-semibold text-gray-900">
              Employment Classification
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-3">
              Exact values recorded on each active employee.
            </p>
            <BreakdownList items={classificationItems} total={activeTotal} />
          </section>

          <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" aria-labelledby="pmet-assessment-heading">
            <h3 id="pmet-assessment-heading" className="text-sm sm:text-lg font-semibold text-gray-900">
              PMET Assessment
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-3">
              {pmet.definition || 'SSOC 2024 Major Groups 1–3'}; independent of salary and pass type.
            </p>
            <BreakdownList items={pmetItems} total={activeTotal} />
          </section>
        </div>

        <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" aria-labelledby="work-authorisation-heading">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
            <div>
              <h3 id="work-authorisation-heading" className="text-sm sm:text-lg font-semibold text-gray-900">
                Residency &amp; Work Authorisation
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Citizenship or employment-pass status, shown against the full active workforce.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs sm:text-sm flex-shrink-0">
              <span className="font-semibold text-green-700 tabular-nums">{current.total_local || 0} local</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span className="font-semibold text-orange-700 tabular-nums">{current.total_foreign || 0} foreign</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            <div>
              {passTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={passTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={88}
                      paddingAngle={2}
                      dataKey="value"
                      label={false}
                    >
                      {passTypeData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} (${percentage(value, activeTotal)}%)`, name]}
                      contentStyle={CHART_TOOLTIP_STYLE}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-52 flex items-center justify-center text-gray-500 text-sm">
                  No work-authorisation data available
                </div>
              )}
            </div>
            <BreakdownList items={passBreakdownItems} total={activeTotal} />
          </div>
        </section>

        {trendData.length > 1 && (
          <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" aria-labelledby="composition-trend-heading">
            <h3 id="composition-trend-heading" className="text-sm sm:text-lg font-semibold text-gray-900 mb-4">
              Local &amp; Foreign Trend
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke={CHART_AXIS_COLOR} />
                <YAxis tick={{ fontSize: 12 }} stroke={CHART_AXIS_COLOR} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Legend />
                <Line type="monotone" dataKey="local" name="Local Employees" stroke="#16a34a" strokeWidth={2} />
                <Line type="monotone" dataKey="foreign" name="Foreign Employees" stroke="#ea580c" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default WorkforceCompositionModal;

